// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.DashboardStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import { observable, action, computed } from 'mobx';
import fetcher from 'fetchers/Izenda.DashboardDataFetcher';
import reportFetcher from 'fetchers/Izenda.ReportDesignerDataFetcher';
import DashboardTile from 'logic/models/Dashboard/Izenda.DashboardTile';
import exportFetcher from 'fetchers/Izenda.ExportFetcher';
import reportsDataFetcher from 'fetchers/Izenda.ReportsDataFetcher';
import Dashboard from './models/Dashboard/Izenda.Dashboard';
import uiState from 'logic/Izenda.UiState';
import constants from 'logic/data/Izenda.DashboardConstants';
import reportDesignerConstants from 'logic/data/Izenda.ReportDesignerConstants';
import DashboardFilterStore from './Izenda.DashboardFilterStore';
import SubscribedInstancesStore from 'logic/Izenda.SubscribedInstancesStore';
import ObjectStateConstants from 'logic/data/Izenda.ObjectStateConstants';
import AccessStore from 'logic/Izenda.AccessStore';
import localizer from 'share/localization/Izenda.Localizer';
import accessConstants from 'logic/data/Izenda.AccessConstants';
import embededReportUtils from 'share/utils/Izenda.EmbededReportUtils';
import { REPORT_PROPERTY } from 'logic/data/Izenda.ReportsConstants';
import { concatTree, parseToTree } from 'share/utils/Izenda.LazyLoadingUtils';
import CategoryDetail from 'logic/models/Izenda.CategoryDetail';
import PillDefinition from 'logic/models/Izenda.PillDefinition';
import DashboardWorker from 'worker-loader?inline&name=izenda_worker.js!share/workers/Izenda.DashboardWorker.js';
import SecurityManager from 'share/authorization/Izenda.SecurityManager';
import { PERMISSION_KEY } from 'logic/data/Izenda.AuthorizationConstants';
import { getApiInfo } from 'share/workers/utils/Izenda.WorkerUtils';
import WORKER_CONSTANTS from 'share/workers/logic/data/Izenda.WorkerConstants';
import PrintingUtils from 'share/utils/Izenda.PrintingUtils';
import ColorPaletteStore from 'logic/Izenda.ColorPaletteStore';
import { NO_PERMISSION } from 'logic/data/Izenda.ReportsConstants';
import cache, { useCacheInAPISide } from 'share/utils/Izenda.CacheInAPISideAttribute';
import CachingConfigurationStore from 'logic/Izenda.CachingConfigurationStore';

import {
  isNil,
  isEmpty,
  assign,
  get,
  omit,
  isFunction,
  map,
  pick,
  forEach,
  findIndex,
  getOr,
  find,
  concat,
  uniq,
  flow,
  filter,
  includes,
  reduce,
  set,
  compact,
  ary,
  isArray,
  head,
  isUndefined
} from 'lodash/fp';

@cache
export default class DashboardStore {
  constructor() {
    this.contentLazyLoadingStore = {};
    this.leftMenuLazyLoadingStore = {};
    this.dashboardWorkerPendingList = [];
    this.relatedDashboardPartIds = [];
    this.printingMode = false;
    this.draftId = null;
    this.operatorGroup = null;
    this.setUseCache();

    // Color palettes for dashboard tile
    this.colorPalettes = [];
    this.initial();
  }

  @observable model = null;
  @observable selectedCategoryId = null;
  @observable dashboardFilterStore = null;
  @observable subscribedInstancesStore = null;
  @observable accessStore = null;
  @observable isCacheEnable = false;
  @computed
  get categoryTree() {
    return this.model.categoryTree;
  }

  @computed
  get dashboardDetail() {
    return this.model.dashboardDetail;
  }

  @computed
  get dashboardDesignerMode() {
    return this.model.dashboardDesignerMode;
  }

  @computed
  get items() {
    return this.model.items;
  }

  @computed
  get tenantId() {
    return uiState.getTenantId();
  }

  @computed
  get isRootCategory() {
    return this.selectedCategoryId === constants.ALL_CATEGORY_TYPES.ALL_DASHBOARDS.id;
  }

  @computed
  get isUncategorized() {
    return this.selectedCategoryId === constants.EMPTY_GUID;
  }

  @computed
  get isDirty() {
    return (
      this.dashboardDetail.checkDirty() ||
      this.subscribedInstancesStore.model.checkDirty() ||
      this.dashboardFilterStore.reportFilter.checkDirty()
    );
  }

  /**
   * initial dashboard
   * @method initial
   * @return {[type]} [description]
   */
  @action('initial')
  @useCacheInAPISide
  initial() {
    this.model = new Dashboard();
    this.dashboardFilterStore = new DashboardFilterStore(this.model.dashboardDetail);
    this.cachingConfiguration = new CachingConfigurationStore();
    this.cachingConfiguration.loadCachingConfiguration().then((data)=>{
      this.isCacheEnable = getOr(false, 'dataCacheSetting.isEnableDataCache', data);
    });
    uiState.setCheckDirtyFunction(() => this.isDirty);
    this.initSubscription();
    this.operatorGroupPromise = () =>
      this.dashboardFilterStore.fetchFilterOperatorGroup().then(result => {
        this.operatorGroup = result;
        return result;
      });
  }

  /**
   * Destroy check Dirty function
   */
  @action('destroy')
  @useCacheInAPISide
  destroy() {
    uiState.resetCheckDirtyFunction();
    this.operatorGroup = null;
  }

  /**
   * create new dashboard detail
   * @method createNewDashboard
   */
  @action('createNewDashboard')
  @useCacheInAPISide
  createNewDashboard() {
    const isNewDashboard = this.dashboardDetail.dashboard.isNewDashboard;
    if (isNewDashboard) {
      this.initSubscription();
    }

    this.dashboardDesignerMode.resetAlertDetails();
    this.model.createNewDashboard();
    this.dashboardFilterStore = new DashboardFilterStore(this.model.dashboardDetail);
  }

  /**
   * Load all categories
   * @return {[type]}
   */
  loadAllCategories() {
    const model = this.model;

    return fetcher
      .loadAllCategories(this.tenantId)
      .then(action(model.readDashboardCategoryTree.bind(model)))
      .return(null);
  }

  /**
   * lazy loading for categories
   * @param {Object} object object:
   * {
   *  pageIndex: int,
   *  pageSize: int,
   *  type: int
   *  criteria: search filter,
   *  parentIds: []
   *  includeUnCategory: bool
   *  tenantId: tenantId
   * }
   */
  lazyLoadCategories(request: any) {
    return fetcher.loadLazyLoadCategories(request).then(response => {
      const categoryList = response.data || [];
      const categories = categoryList.map(item => {
        return new CategoryDetail(item);
      });

      return {
        categories: categories,
        isLastPage: response.isLastPage
      };
    });
  }

  /**
   * lazy loading for subCategories
   * @param {Object} object object:
   * {
   *  pageIndex: int,
   *  pageSize: int,
   *  type: int
   *  criteria: search filter,
   *  parentIds: []
   *  tenantId: tenantId
   * }
   */
  lazyLoadSubCategories(request: any) {
    return fetcher.loadLazyLoadSubcategories(request).then(response => {
      const subCategoryList = response.data || [];
      const subcategories = subCategoryList.map(item => {
        return new CategoryDetail(item);
      });

      return {
        subCategories: subcategories,
        isLastPage: response.isLastPage
      };
    });
  }

  /**
   * validate category name
   * @param  {object} category
   * @returns {Promise}
   */
  validateCategoryName(category: any) {
    return fetcher.validateCategoryName(category).then(result => {
      return result.messages;
    });
  }

  /**
   * get data for report part in preview mode
   * @method getReportPartData
   * @param  {object} dashboardPart Dashboard Part need update data
   * @return {Promise} Ajax request
   */
  getReportPartData(
    { reportPart, disableViewReport },
    savedMode: boolean,
    drillDownData: any,
    pageIndex: number,
    pageSize: number,
    printingMode: boolean
  ) {
    if (isNil(reportPart) || disableViewReport) {
      return Promise.resolve();
    }

    reportPart.startLoadData();
    const embededReportDefPromise = embededReportUtils.buildPromiseFetcher(
      reportPart,
      reportFetcher.loadReportPartDefinition.bind(reportFetcher),
      reportFetcher.loadForEdit.bind(reportFetcher)
    );
    const colorPaletteStore = new ColorPaletteStore();

    return Promise.join(
      this.getDrilldownInfo.apply(this, arguments),
      embededReportDefPromise,
      colorPaletteStore.loadThemes(),
      action((result, embededReportDef, palettes) => {
        if(result.reportDefinition) {
          const part = find(item => item.reportPart === reportPart, this.dashboardDetail.dashboard.activeDashboardParts);
          if(!isNil(part)){
            const reportPartDefinition = find({ id: reportPart.id }, result.reportDefinition.reportPart);
            const tempStateData = reportPart.getTemporaryStateData();
            part.readReportInfo(result.reportDefinition, reportPartDefinition);
            reportPart.restoreTemporaryStateData(tempStateData);
          }
        }

        reportPart.setQueryData(result);
        if (!result.messages) {
          reportPart.readEmbededReportDef(embededReportDef);
          reportPart.colorPalettes = palettes;
          this.setColorPalettes(palettes);
        }
        // return null instead of undefined
        return result.messages || null;
      })
    ).catch(() => {
      reportPart.setQueryData([]);
    });
  }

  getDrilldownInfo(
    dashboardPart: any,
    savedMode: boolean,
    drillDownData: any,
    pageIndex: number,
    pageSize: number,
    printingMode: boolean
  ) {
    let {
      reportPart,
      reportPart: { reportPartContent },
      reportPart: {
        reportPartContent: { optionalParams = {} }
      },
      reportId,
      reportPartId,
      filters,
      numberOfRecord,
      id,
      dashboardId
    } = dashboardPart;

    if (!reportPartContent.isBeingBuild) {
      return Promise.resolve().return({});
    }

    let request;
    let requestPromise;

    const ignoreCacheParam = arguments[8];
    optionalParams.ignoreCache = !isUndefined(ignoreCacheParam) ? ignoreCacheParam : !this.cacheInAPI;
    if (savedMode && !this.draftId) {
      if (this.printingMode) {
        optionalParams = omit(['paging'], optionalParams);
        optionalParams.loadAll = true;
      }

      if (!this.printingMode) {
        let crossFiltering = this.dashboardDetail.getCrossFilteringForDashboardPart(dashboardPart, false);
        !isEmpty(crossFiltering) && (optionalParams.filters = crossFiltering);
      }

      request = assign({ dashboardPartId: id }, optionalParams);
      requestPromise = fetcher.loadDashboardPartData(request);
    } else {
      // pagination
      if (
        reportPartContent.usePagination ||
        reportPartContent.properties.usePagination ||
        get('properties.view.usePagination', reportPartContent)
      ) {
        optionalParams.paging = {
          pageIndex: pageIndex || reportDesignerConstants.DEFAULT_PAGE_INDEX,
          pageSize: reportPartContent.properties.pageSize || reportDesignerConstants.DEFAULT_PAGE_SIZE
        };
      }

      drillDownData =
        drillDownData || (isFunction(reportPartContent.getDrilldownInfo) && reportPartContent.getDrilldownInfo());
      if (drillDownData) {
        optionalParams.filters = drillDownData.filters || [];
        optionalParams.expandedLevel = !isNil(drillDownData.expandedLevel)
          ? drillDownData.expandedLevel
          : optionalParams.filters.length;
      }

      let crossFiltering = this.dashboardDetail.getCrossFilteringForDashboardPart(dashboardPart, false);
      !isEmpty(crossFiltering) && Array.prototype.push.apply(optionalParams.filters || [], crossFiltering);

      if (this.printingMode) {
        optionalParams = omit(['paging'], optionalParams);
        optionalParams.loadAll = true;
      }

      let reportPartState;
      if (reportPart.type === reportDesignerConstants.REPORT_PART_TYPE.Map) {
        reportPartState = get('properties.reportPartState', reportPartContent);
      }

      request = {
        dashboardPart: {
          reportId: reportId,
          reportPartId: reportPartId,
          filters: map(pick(['filterFieldId', 'value', 'operatorId']), filters),
          dashboardId: dashboardId,
          numberOfRecord: numberOfRecord,
          reportPartState: reportPartState
        },
        dataRequest: optionalParams || {}
      };
      requestPromise = fetcher.previewDashboardPartData(request);
    }

    return requestPromise;
  }

  /**
   * Validate Dashboard Name
   * @param {Object} boardDetail
   * @returns {Promise}
   */
  validateName({ id, name, categoryId, subCategoryId }) {
    const data = {
      tenantId: this.tenantId,
      dashboardId: id,
      name: name,
      categoryId: categoryId,
      subCategoryId: subCategoryId
    };

    return fetcher.validateDashboardName(data).then(function (result) {
      return result.messages;
    });
  }


  /**
   * Validate Dashboard NLQ Name
   * @param {Object} boardDetail
   * @returns {Promise}
   */
  validateNameDashboardNLQ({ id, name, categoryId, subCategoryId }) {
    const data = {
      tenantId: this.tenantId,
      dashboardId: id,
      name: name,
      categoryId: categoryId,
      subCategoryId: subCategoryId
    };

    return fetcher.validateDashboardName(data).then(function (result) {
      return result;
    });
  }

  /**
   * set dashboard type for dashboard part
   * @method setDashboardTileType
   * @param  {[type]}             dashboardPart [description]
   * @param  {[type]}             type          [description]
   * @param  {[type]}             reportPart    [description]
   */
  @action('setDashboardTileType')
  @useCacheInAPISide
  setDashboardTileType(dashboardPart: any, type: string, reportPart: any, report: any) {
    const dashboardDetail = this.dashboardDetail;

    // check if user is changing report part in dash board -> remove all cross filtering in that dashboard part.
    !isNil(dashboardPart.reportPart) && this.removeRelatedDrillDownInfo(dashboardPart);

    if (isNil(reportPart)) {
      dashboardDetail.setType(dashboardPart, type);
      return Promise.resolve();
    }

    const getCommonData = () => {
      return this.getCommonFilters().then(() => this.getReportPartData(dashboardPart));
    };

    const { id: reportPartId } = reportPart;
    const { id: reportId } = report;

    return this.associateReportDefToDashboardPart(
      dashboardPart,
      reportId,
      reportPartId,
      action((reportDef, reportPartDef) => {
        dashboardDetail.setType(dashboardPart, type, reportPartDef, reportDef);
      })
    ).then(getCommonData);
  }

  /**
   * get common filter
   * @method getCommonFilters
   * @return {[type]}         [description]
   */
  getCommonFilters(commonFilters, shouldUpdateFilterData) {
    const dashboardDetail = this.dashboardDetail;
    const dashboardFilterStore = this.dashboardFilterStore;

    return fetcher
      .loadCommonFilters(dashboardDetail.getDashboardDefinition())
      .then(data => {
        isArray(data) && dashboardDetail.readCommonFilters(data, commonFilters);
        return true;
      })
      .then(dashboardFilterStore.getFilterData.bind(dashboardFilterStore, null, null, shouldUpdateFilterData, true));
  }

  /**
   * Update filters' value of dashboard part with value from common filters' value.
   * @param  {Object} dashboardPart
   */
  @action('mapCommonFilterValueToDashboardPartFilter')
  mapCommonFilterValueToDashboardPartFilter({ filters }) {
    const commonFilterFields = this.model.dashboardDetail.dashboard.commonFilterFields;

    forEach(commonFilter => {
      forEach(filterField => {
        const matchedIndex = findIndex(item => {
          return item.filterFieldId === filterField.filterFieldId;
        }, filters);

        if (matchedIndex !== -1) {
          filters[matchedIndex].value = commonFilter.value;
          // Handle update state of filter
          if (isNil(filters[matchedIndex].state) || filters[matchedIndex].state === ObjectStateConstants.INSERT) {
            filters[matchedIndex].updateState(ObjectStateConstants.INSERT);
          } else {
            filters[matchedIndex].updateState(ObjectStateConstants.UPDATE);
          }
        }
      }, commonFilter.filterFields);
    }, commonFilterFields);
  }

  /**
   * refresh dashboard part
   * @method refreshDashboardPart
   * @param  {[type]}             dashboardPart [description]
   * @return {[type]}                           [description]
   */
  @useCacheInAPISide
  refreshDashboardPart(dashboardPart: any) {
    this.mapCommonFilterValueToDashboardPartFilter(dashboardPart);
    return this.getReportPartData(dashboardPart, !dashboardPart.checkDirty());
  }

  /**
   * Validate dashboard name
   * @param {Object} dashboard detail model
   */
  validateDashboardName(dashboardDetail: any) {
    const data = dashboardDetail.getInitialData();
    return fetcher.validateDashboardName(data).then(result => result.messages);
  }

  /**
   * Validate Dashboard Name
   * @param {Object} boardDetail
   * @returns {Promise}
   */
  rename(boardDetail: any) {
    return fetcher
      .renameDashboard({
        name: boardDetail.name,
        dashboardId: boardDetail.id
      })
      .then(result => result);
  }

  /**
   * Save dashboard
   * @param {Object} cloned dashboard model
   * @returns {Object}
   */
  @action('saveDashboard')
  saveDashboard(clonedModel: any) {
    const model = isEmpty(clonedModel) ? this.model : clonedModel;
    const dashboardDefinition = model.dashboardDetail.buildDataForSaving();

    function updateAccess(dashboardId) {
      this.accessStore.updateReporId(dashboardId);
      this.accessStore.loadAllAccess(dashboardId);
    }

    return fetcher
      .saveDashboard(dashboardDefinition)
      .bind(this)
      .then(
        action(result => {
          if (result.success) {
            // Remove all Cross Filtering after saving successfully.
            this.dashboardDetail.removeAllDrillDowns();
            this.dashboardDetail.removeAllCrossFilterings();
            // Load for left navigation
            this.loadAllCategories();
            // Switch edit mode
            this.dashboardDesignerMode.setEditMode();
            // Reset need update result
            this.dashboardFilterStore.reportFilter.resetUpdateResult();
            // set Alert messages
            this.dashboardDesignerMode.setAlertDetails({
              success: true,
              message: localizer.getText('COMMON_MESSAGES_DETAILS_WERE_SAVED')
            });

            // get access here
            const authorization = new SecurityManager();
            if (authorization.checkPrivileges([PERMISSION_KEY.DashboardsActionsConfigureAccessRights])) {
              let initAccessPromise;
              if (isEmpty(this.accessStore)) {
                initAccessPromise = this.initAccess();
              }

              if (initAccessPromise) {
                initAccessPromise.then(() => {
                  updateAccess.call(this, result.dashboard.id);
                });
              } else {
                updateAccess.call(this, result.dashboard.id);
              }
            }

            if (
              authorization.checkPrivileges([
                PERMISSION_KEY.DashboardsActionsSubscribe,
                PERMISSION_KEY.DashboardsActionsSchedule
              ])
            ) {
              // Load subscriptions
              this.subscribedInstancesStore.dashboardId = result.dashboard.id;
              this.subscribedInstancesStore.loadInstances();
            }

            // Load dashboard
            return this.readDashboardDefinition(result.dashboard, null, true).return(result);
          }

          return result;
        })
      );
  }

  /**
   * Load dashboard
   * @param {Object} id[dashboard id]
   * @returns {Object}
   */
  @action('loadDashboard')
  loadDashboard(id: string, commonFilters: any, pageId: string, isCanceled: boolean) {
    const dashboardParams = { dashboardId: id, action: isCanceled ? 2 : 1 };

    !isNil(pageId) && (dashboardParams.page = pageId);
    const loadDashboardPromise = this.draftId
      ? fetcher.getDraftDataForPrint(this.draftId)
      : fetcher.loadDashboard(dashboardParams).get('dashboard');
    const colorPaletteStore = new ColorPaletteStore();

    return Promise.join(
      loadDashboardPromise,
      !this.draftId && fetcher.loadRelatedDashboardParts(id),
      colorPaletteStore.loadThemes(),
      action((dashboard, relatedDashboardPartIds, palettes) => {
        this.readDashboardDefinition(dashboard, commonFilters);
        this.relatedDashboardPartIds = relatedDashboardPartIds;
        this.setColorPalettes(palettes);
        !this.draftId && this.startDashboardDataWorker(0);
        return Promise.resolve();
      })
    );
  }

  /**
   * Load dashboard list
   * @param  {string} catId
   * @return {array}
   */
  @action('loadDashboards')
  loadDashboards(catId: string) {
    catId && (this.selectedCategoryId = catId);
    let dataObject = {
      tenantId: this.tenantId,
      isUncategorized: this.isUncategorized
    };
    const items = this.items;

    if (!this.isRootCategory && !this.isUncategorized) {
      dataObject.criterias = [
        {
          key: constants.CATEGORY_TYPE.CATEGORY_ID,
          value: this.selectedCategoryId
        }
      ];
    }

    return fetcher
      .loadDashboardCategories(dataObject)
      .bind(this)
      .then(data => {
        items.readBoardList(data);
        return data;
      })
      .then(() => this.loadAllCategories());
  }

  /**
   * Delete dashboard
   * @param {Object} dashboard id
   * @param {Object} dashboardSubCategory
   * @returns {Promise}
   */
  deleteDashboard(dashboardId: string, dashboardSubCategory: any) {
    return fetcher
      .deleteDashboard(dashboardId)
      .bind(this)
      .then(
        action(result => {
          return result && this.removeDashboard(dashboardId, dashboardSubCategory);
        })
      );
  }

  /**
   * delete dashboard part
   * @method deleteDashboardPart
   * @param  {[type]}            dashboardPart [description]
   * @return {[type]}                          [description]
   */
  @action('deleteDashboardPart')
  deleteDashboardPart(dashboardPart: any) {
    this.dashboardDetail.deleteDashboardPart(dashboardPart);
    this.removeRelatedDrillDownInfo(dashboardPart);
    // No need to load common filters when delete not-report-part type
    return dashboardPart.type === constants.TILE_TYPE.reportPart.type
      ? this.getCommonFilters(null, true)
      : Promise.resolve();
  }

  /**
   * Remove dashboard from sub categories
   * @param {type} dashboardId
   * @param {type} dashboardSubCategory
   */
  @action('removeDashboard')
  removeDashboard(dashboardId: string, boardSubCategory: any) {
    const dashboardItem = find({ id: dashboardId }, boardSubCategory.dashboards);
    boardSubCategory.dashboards.remove(dashboardItem);

    if (boardSubCategory.dashboards.length === 0) {
      this.lazyLoadingDashboards(null, true, null);
      this.loadLazyLoadingCategories(true);
    }

    return null;
  }

  /**
   * Read Dashboard Definition
   * @param {Object} id[dashboard id]
   * @returns {Object}
   */
  @action('readDashboardDefinition')
  readDashboardDefinition(dashboardDefinition: any, commonFilters: any, shouldUpdateFilterData: boolean) {
    const dashboardDetail = this.model.dashboardDetail;

    dashboardDetail.readDashboardDefinition(dashboardDefinition, commonFilters);
    dashboardDetail.cleanDirty();

    return this.getCommonFilters(commonFilters, shouldUpdateFilterData);
  }

  /**
   * Move dashboard to another category or subcategory
   * @param {Object} data move data
   * @returns {Object} Promise
   */
  moveDashboard(data: any) {
    return fetcher.moveDashboard(Object.assign({ tenantId: this.tenantId }, data));
  }

  /**
   * Copy dashboard
   * @param {Object} data move data
   * @returns {Object} Promise
   */
  copyDashboard(data: any) {
    return fetcher.copyDashboard(Object.assign({ tenantId: this.tenantId }, data));
  }

  /**
   * Save dashboard subscriptions
   * This is used for save subscribed instances in dashboard list.
   * @param {String} dashboardId
   * @param {Array} subscriptions
   * @returns {Promise}
   */
  saveDashboardSubscriptions(dashboardId: string, subscriptions: any) {
    const data = {
      id: dashboardId,
      subscriptions: subscriptions
    };

    return fetcher.saveSubscriptions(data);
  }

  /**
   * copy dashboardPart
   * @method copyDashboardPart
   * @param  {DashboardTile}          dashboardPart Original DashboardPart
   * @return {Promise}                        [description]
   */
  @action('copyDashboardPart')
  @useCacheInAPISide
  copyDashboardPart(dashboardPart: any) {
    const newDashboardPart = this.dashboardDetail.copyDashboardPart(dashboardPart);
    this.loadDashboardPart(newDashboardPart);
    if (!newDashboardPart.isReportPartType) {
      return Promise.resolve();
    }

    return this.getCommonFilters();
  }

  /**
   * cancel dashboard
   * @method cancelDashboard
   * @return {Promise}        [description]
   */
  @action('cancelDashboard')
  cancelDashboard() {
    const {
      preset,
      dashboard: { id, isNewDashboard }
    } = this.dashboardDetail;
    this.createNewDashboard();

    if (isNewDashboard) {
      this.initSubscription();
      this.dashboardDetail.readPreset(preset);
      return Promise.resolve();
    }

    return this.loadDashboard(id, undefined, undefined, true);
  }

  /**
   * validate backgroundImage
   */
  @action('validateBackgroundImage')
  validateBackgroundImage() {
    const dashboardDesignerMode = this.dashboardDesignerMode;

    this.validateImageUrl(this.dashboardDetail.dashboard.imageUrl).then((message: string) => {
      !isEmpty(message) && dashboardDesignerMode.setAlertDetails({ message: message });
    });
  }

  /**
   * validate image url can access
   * @type {[type]}
   */
  validateImageUrl(imageUrl: string) {
    if (!imageUrl) {
      return Promise.resolve('');
    }

    const validatePromise = new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = function () {
        resolve();
      };
      img.onerror = function () {
        reject();
      };
      img.src = imageUrl;
    });

    return validatePromise.then(() => '').catch(() => constants.MESSAGE.INVALID_IMAGE_URL);
  }

  /**
   * Update results
   */
  updateResults(loadMode: any) {
    const loadFunction = loadMode ? this.refreshDashboardPart.bind(this) : this.getReportPartData.bind(this);
    return Promise.map(this.dashboardDetail.dashboard.reportPartDashboardParts, ary(1, loadFunction)).then(() =>
      this.dashboardFilterStore.reportFilter.resetUpdateResult()
    );
  }

  /**
   * Send Email
   * @param {string} dashboardId
   * @param {object} email
   */
  sendEmail(dashboardId: string, email: string) {
    return exportFetcher.email({ dashboardId: dashboardId }, email);
  }

  /**
   * Load definition of dashboard part.
   * @param  {String} id
   */
  loadDashboardPartDefinition(id: string) {
    return fetcher.loadDashboardPartDefinition(id);
  }

  /**
   * Load definition of report part.
   * @param  {String} id
   */
  loadReportPartDefinition(id: string) {
    return reportFetcher.loadReportPartDefinition(id);
  }

  /**
   * Go to subscription page
   */
  @action('initSubscription')
  initSubscription() {
    const isSubscribe = this.model.dashboardDesignerMode.isSubscribe;
    this.subscribedInstancesStore = new SubscribedInstancesStore(true, this, isSubscribe);
    this.subscribedInstancesStore.model.setType(isSubscribe);
  }

  /**
   * Init access, user, role store.
   */
  @action('initAccess')
  initAccess() {
    if (this.accessStore) return;
    const ACCESS_MODE = accessConstants.ACCESS_MODE.DASHBOARD;
    const {
      accessModel,
      dashboard: { id, isNewDashboard, isGlobal }
    } = this.model.dashboardDetail;

    this.accessStore = new AccessStore(accessModel, id, ACCESS_MODE);
    this.accessStore.updateReporId(id);
    return this.accessStore.initialAccessDashboardPage(id, ACCESS_MODE, isGlobal, isNewDashboard);
  }

  /**
   * Validate access.
   * @return {object} promise result
   */
  validateAccesses() {
    function validate() {
      const { id, ownerId } = this.model.dashboardDetail.dashboard;
      const data = {
        id: id,
        ownerId: ownerId,
        accesses: this.accessStore.model.accessList
      };
      return fetcher.validateAccesses(data);
    }

    if (new SecurityManager().checkPrivileges([PERMISSION_KEY.DashboardsActionsConfigureAccessRights])) {
      let initAccessPromise;
      if (isEmpty(this.accessStore)) {
        initAccessPromise = this.initAccess();
      }

      if (initAccessPromise) {
        return initAccessPromise.then(() => validate.apply(this));
      }
      return validate.apply(this);
    } else {
      return Promise.resolve({ success: true });
    }
  }

  /**
   * Validate Dashboard
   * @param {type} clonedModel
   */
  validateDashboard(clonedModel: any) {
    const model = isEmpty(clonedModel) ? this.model : clonedModel;
    const dashboardDefinition = model.dashboardDetail.buildDataForSaving();

    return fetcher.validateDashboard(dashboardDefinition.dashboard);
  }

  /**
   * Load dashboard part data
   * @param {Object} dashboardPart
   * @param {Boolean} printingMode
   */
  @action('loadDashboardPart')
  @useCacheInAPISide
  loadDashboardPart(dashboardPart: any, printingMode: boolean) {
    if (!dashboardPart.reportId) {
      return Promise.resolve();
    }

    const { reportId, reportPartId } = dashboardPart;
    return this.associateReportDefToDashboardPart(
      dashboardPart,
      reportId,
      reportPartId,
      action((reportDef: any, reportPartDef: any) => {
        dashboardPart.readReportInfo(reportDef, reportPartDef);
      }),
      printingMode
    ).then(() => {
      // when switching between configuration mode and view mode,
      // dashboardPart will be reloaded and removed all drillDown info.
      this.removeRelatedDrillDownInfo(dashboardPart);
      this.getReportPartData(dashboardPart, !dashboardPart.checkDirty(), null, null, null, printingMode);
    });
  }

  /**
   * Get allowed saving categories
   * @param {type} clonedModel
   */
  getAllowedSavingCategories(clonedModel: any) {
    return fetcher.getAllowedSavingCategories(this.tenantId).then(categories => {
      clonedModel.getAllCategory(categories);
    });
  }

  /**
   * Export report part on dashboard
   * @param  {string} url
   * @param  {string} format    export format
   * @param  {string} reportId  report id
   * @param  {array}  filters    report filters
   * @param  {string} dashboard part id
   * @param  {object} successCallBack
   * @param  {object} failCallBack
   * @return {Promise}
   */
  exportReportPart = function (
    url: string,
    format: string,
    reportPartId: string,
    reportId: string,
    filters: any,
    dashboardPartId: string,
    successCallBack: () => mixed,
    failCallBack: () => mixed
  ) {
    const filtersData = map(filter => filter.filterField, filters);
    const dashboardDetail = this.dashboardDetail;
    const dashboardDefintion = dashboardDetail.getDashboardDefinition();
    const activeDashboard = findIndex({ id: dashboardPartId }, dashboardDetail.dashboard.dashboardParts);

    return exportFetcher.export(
      {
        url: url,
        dashboardPartIndex: activeDashboard,
        dashboardDefinition: JSON.stringify(dashboardDefintion),
        format: format,
        reportId: reportId,
        reportPartId: reportPartId,
        filters: JSON.stringify(filtersData),
        tenantId: this.tenantId // for export API.
      },
      successCallBack,
      failCallBack
    );
  };

  /**
   * Load all neccessary report data (report definition, filters...) for
   * dashboard part and associate report def with current dashboard part.
   * @param [Object] Dashboard part
   * @param [String] Report id
   * @param [Boolean] Indicate that it should load actual report part data or preview data.
   * @param [Boolean] Indicate the current dashboard part should reset its dirty tracking
   * @param [associateReportDef] A callback that get report definition
   */
  @action('associateReportDefToDashboardPart')
  associateReportDefToDashboardPart(
    dashboardPart: any,
    reportId: string,
    reportPartId: string,
    associateReportDef: any,
    printingMode: boolean
  ) {
    const { reportFilter } = this.dashboardFilterStore;
    return reportFetcher.validateReportId(reportId).then(reportStatus => {
      const reportIdForLoad = printingMode ? reportId : null;
      return Promise.join(
        reportsDataFetcher.reportByProperty(reportId, REPORT_PROPERTY.Filter).catch(error => error.responseJSON),
        reportFetcher.loadReportPartDefinition(reportPartId, reportIdForLoad),
        this.loadOperatorGroupPromise(),
        action((reportDef, reportPartDef, operatorGroup) => {
          if (!reportStatus.success && !isNil(reportDef)) {
            reportDef.detail = {
              message: get('messages[0].messages[0]', reportStatus)
            };
          }
          associateReportDef(reportDef, reportPartDef);
          dashboardPart.buildConfigFieldList(operatorGroup, reportFilter.markUpdateResult.bind(reportFilter));
          return null;
        })
      );
    });
  }

  /**
   * update renderingTime
   * @param {Number} renderingTime
   */
  updateRenderingTime(renderingTime: number) {
    const dashboardId = this.model.dashboardDetail.dashboard.id;
    return fetcher.updateRenderingTime(dashboardId, renderingTime);
  }

  /**
   * resolve URL options params
   * @method readURLOptions
   * @param  {[type]}       query [description]
   * @return {[type]}             [description]
   */
  readURLOptions(query: string) {
    this.printingMode = !!getOr(false, 'print', query);
    this.printingMode && (this.draftId = getOr(null, 'draftId', query));
  }

  /**
   * Update common filter
   * @param  {stirng} id
   * @return {Promise}
   */
  updateCommonFilter(id: string) {
    return fetcher
      .getCommonFilterById(id)
      .bind(this)
      .then(action(result => this.model.dashboardDetail.updateCommonFilter(result)));
  }

  /**
   * save draft dashboard definition and return draft id to build URL
   * @method saveDraftDataForPrint
   * @return {Promise}              [description]
   */
  saveDraftDataForPrint() {
    const dashboardDefinition = this.model.dashboardDetail.getDashboardDefinition();
    return fetcher.saveDraftDataForPrint(dashboardDefinition).get('id');
  }

  /**
   * Lazy load dashboard list
   * @param  {String} catId
   * @param  {Boolean} isReset
   * @return {Promise}
   */
  @action('lazyLoadingDashboards')
  lazyLoadingDashboards(catId: string, isReset: boolean, isGlobal: boolean, pageInfo: any) {
    this.selectedCategoryId = catId;
    pageInfo = pageInfo || this.contentLazyLoadingStore.pageInfoForFirstTime;

    const dataObject = {
      tenantId: this.tenantId,
      isUncategorized: this.isUncategorized,
      skipItems: pageInfo.fromIndex,
      pageSize: pageInfo.pageSize,
      parentIds: pageInfo.parentIds,
      includeGlobalCategory: true,
      isGlobal: this.parseIsGlobalValue(isGlobal)
    };

    if (!this.isRootCategory && !this.isUncategorized) {
      dataObject.criterias = [
        {
          key: constants.CATEGORY_TYPE.CATEGORY_ID,
          value: this.selectedCategoryId
        }
      ];
    }

    return fetcher
      .lazyLoadingDashboards(dataObject, true)
      .bind(this)
      .then(action(result => this.items.populate(result, isReset, constants.DASHBOARD_LIST_CONCAT_TREE_MAP)));
  }

  /**
   * Lazy search dashboard list
   * @returns {Promise}
   */
  lazyLoadingSearch(isGlobal: boolean, pageInfo: any) {
    const model = this.items;
    const isReset = isNil(pageInfo);
    pageInfo = pageInfo || this.contentLazyLoadingStore.pageInfoForFirstTime;
    let request = {
      criterias: [],
      isUncategorized: this.isUncategorized,
      sortCriteria: {
        key: model.sortBy,
        descending: model.sortDir === constants.SORT_TYPES.ASC ? false : true
      },
      tenantId: this.tenantId,
      skipItems: pageInfo.fromIndex,
      pageSize: pageInfo.pageSize,
      parentIds: pageInfo.parentIds,
      includeGlobalCategory: true,
      isGlobal: this.parseIsGlobalValue(isGlobal)
    };

    // handling criterias
    if (!this.isRootCategory && !isNil(this.selectedCategoryId)) {
      request.criterias.push({
        key: constants.CATEGORY_TYPE.CATEGORY_ID,
        value: this.selectedCategoryId
      });
    }

    (!isNil(model.searchType) || !isNil(model.searchText)) &&
      request.criterias.push({
        key: model.searchType,
        value: model.searchText
      });

    return fetcher
      .lazyLoadingSearchCategories(request)
      .bind(this)
      .then(result => this.items.populate(result, isReset, constants.DASHBOARD_LIST_CONCAT_TREE_MAP));
  }

  /**
   * Load all categories
   * @param  {Number} reportType
   * @return {array}
   */
  @action('loadLazyLoadingCategories')
  loadLazyLoadingCategories(isReset: boolean, pageInfo: any) {
    pageInfo = pageInfo || this.leftMenuLazyLoadingStore.pageInfoForFirstTime;
    const param = {
      tenantId: this.tenantId,
      skipItems: pageInfo.fromIndex,
      pageSize: pageInfo.pageSize,
      parentIds: pageInfo.parentIds,
      includeGlobalCategory: true
    };

    // TODO: null report type on view mode.
    return fetcher
      .loadLazyLoadingCategories(param)
      .bind(this)
      .then(
        action(result => {
          const data = result.data;
          const categories = map(item => {
            const globalLocalCategory = new CategoryDetail();
            globalLocalCategory.populateDataForGlobalLocalCategory(item);
            return globalLocalCategory;
          }, data);

          if (isReset) {
            this.model.leftMenuCategories.splice(0, this.model.leftMenuCategories.length);
          }

          if (!isEmpty(this.model.leftMenuCategories)) {
            this.model.leftMenuCategories.shift();
          }

          this.model.isLeftMenuLastPage = result.isLastPage;
          concatTree(
            this.model.leftMenuCategories,
            categories,
            constants.LEFT_MENU_CONCAT_TREE_MAP,
            result.numOfChilds,
            []
          );

          this.model.leftMenuCategories.splice(0, 0, this.model.createAllItem());
          this.model.leftMenuCategoryTree = parseToTree(this.model.leftMenuCategories, constants.LEFT_MENU_TREE_MAP);

          return Promise.resolve();
        })
      );
  }

 /**
   * Load all categories
   * @param  {Number} reportType
   * @return {array}
   */
  loadDashboardList(pageInfo: any){
    const param = {
      tenantId: this.tenantId,
      skipItems: pageInfo.fromIndex,
      pageSize: pageInfo.pageSize,
      parentIds: pageInfo.parentIds,
      includeGlobalCategory: true
    };
    return fetcher
    .lazyLoadingDashboards(param);
  }

  /**
   * Register content lazy loading store
   * @param {Object} lazyLoadingStore
   */
  registerContentLazyLoadingStore(lazyLoadingStore: any) {
    this.contentLazyLoadingStore = lazyLoadingStore;
  }

  /**
   * Register left menu lazyLoadingStore
   */
  registerLeftMenuLazyLoadingStore(lazyLoadingStore: any) {
    this.leftMenuLazyLoadingStore = lazyLoadingStore;
  }

  /**
   * Change global dashboard value
   * @param {boolean} value
   */
  @action('changeGlobalType')
  changeGlobalType(value: boolean) {
    this.dashboardDetail.dashboard.isGlobal = value;
    this.dashboardDetail.markDirty();
  }

  /**
   * Validate global dashboard if it is containing any local report?
   */
  validateGlobalDashboard(dashboardDefinition: any) {
    return fetcher.validateGlobalDashboard(dashboardDefinition);
  }

  /**
   * Parse IsGlobal Value
   * @param  {any} value
   * @return {any}
   */
  parseIsGlobalValue(value: any) {
    switch (value) {
      case 'true':
        return true;
      case 'false':
        return false;
      case 'null':
        return null;
      case undefined:
        return null;
      default:
        return value;
    }
  }

  /**
   * clicked on remove Pill
   * @param pillDefinition: pill which is being removed.
   */
  @action('onRemovingPillHandler')
  @useCacheInAPISide
  onRemovingPillHandler(pillDefinition: PillDefinition) {
    let dashboardPart = find(
      dashboardPart => dashboardPart.id === pillDefinition.parentId,
      this.dashboardDetail.dashboard.dashboardParts
    );
    this.dashboardDetail.onClearDrillDown(dashboardPart);
    this.getReportPartData(dashboardPart, false, null, null, null, false);
    this.onDashboardPartDrillDown(dashboardPart);
    this.dashboardDetail.calculateCrossFilteringColors();
  }

  /**
   * Removing all pills, click removed all
   */
  @action('onRemovingAllPillHandler')
  @useCacheInAPISide
  onRemovingAllPillHandler() {
    let reportIds = [];
    forEach(drillDownInfo => {
      reportIds = concat(reportIds, [drillDownInfo.dashboardPart.reportId]);
    }, this.dashboardDetail.dashboard.dashboarPartDrillDowns);
    reportIds = uniq(reportIds);

    this.dashboardDetail.removeAllDrillDowns();
    this.dashboardDetail.removeAllCrossFilterings();

    let activeDashboardParts = get('dashboard.activeDashboardParts', this.dashboardDetail);

    forEach(reportId => {
      flow(
        filter(dashboardPart => dashboardPart.reportId === reportId),
        forEach(dashboardPart => {
          this.dashboardDetail.onClearDrillDown(dashboardPart);
          this.getReportPartData(dashboardPart, false, null, null, null, false);
        })
      )(activeDashboardParts);
    }, reportIds);
  }

  /**
   * action when drilldown in dashboard part.
   * @param: dashboardPart: dirilling down dashboard part.
   */
  @action('onDashboardPartDrillDown')
  @useCacheInAPISide
  onDashboardPartDrillDown(dashboardPart: DashboardTile) {
    if (
      !this.dashboardDetail.isCrossFiltering(dashboardPart) ||
      !this.dashboardDetail.isAllowedCrossFiltering(dashboardPart)
    ) {
      return;
    }
    this.dashboardDetail.updateDrillDownCollection(dashboardPart);
    this.reloadRelatedCrossFilteringDashboardParts(dashboardPart);
    this.processDashboardPartIsLoading(dashboardPart.id);
    this.dashboardDetail.onDrillDownUpdatePill(dashboardPart);
    this.dashboardDetail.calculateCrossFilteringColors();
  }

  /**
   * Process for the dashboard part is loading
   * @param {String} id
   */
  processDashboardPartIsLoading(id: string) {
    const pendingWorkerList = this.dashboardWorkerPendingList;
    flow(
      find(partId => includes(id, partId)),
      filter(partId => partId !== id),
      map(partId => find(workerPending => workerPending.dashboardPart.id === partId, pendingWorkerList)),
      filter(pendingWorker => !isNil(pendingWorker)),
      forEach(loadingPart => {
        loadingPart.worker.terminate();
        this.createDashboardPartWorker(loadingPart.dashboardPart, loadingPart.index);
      })
    )(this.relatedDashboardPartIds);
  }

  /**
   * after deleting dashboard part.
   * do some logic to remove cross Filtering, remove related Pill, reload data.
   * @param dashboardPart, dashboard tile which is removing related drilldown.
   */
  @action('removeRelatedDrillDownInfo')
  removeRelatedDrillDownInfo(dashboardPart: DashboardTile): void {
    if (!this.dashboardDetail.isCrossFiltering(dashboardPart)) {
      return;
    }
    const relatedDrillDown = this.dashboardDetail.getRelatedDrillDown(dashboardPart);
    if (relatedDrillDown) {
      // remove related drill down if any.
      this.dashboardDetail.removeDrillDownInCollection(dashboardPart);
      // remove it own pill
      this.dashboardDetail.removePill(get('report.id', dashboardPart), get('id', dashboardPart));
      // reload all related dashboardParts
      this.reloadRelatedCrossFilteringDashboardParts(dashboardPart);
      this.dashboardDetail.calculateCrossFilteringColors();
    }
  }

  /**
   * Get all related Cross Filtering Dashboard, then do reload data for each DashboardPart. except the currentDashboardParts.
   * @param: dashboardPart, dashboard tile which's related dashboard parts need to be reloaded.
   */
  @action('reloadRelatedCrossFilteringDashboardParts')
  @useCacheInAPISide
  reloadRelatedCrossFilteringDashboardParts(dashboardPart: DashboardTile): void {
    let relatedDashboardParts = this.dashboardDetail.getRelatedCrossFilteringDashboardParts(dashboardPart);
    !isEmpty(relatedDashboardParts) &&
      forEach(dashboardPart => {
        this.getReportPartData(dashboardPart, false, null, null, null, false);
      }, relatedDashboardParts);
  }

  /**
   * Start to load dashboard data worker
   * @param {Number} start
   */
  @action('startDashboardDataWorker')
  startDashboardDataWorker(start: number) {
    const parts = filter(
      dashboardPart => !isNil(dashboardPart.reportId),
      this.dashboardDetail.dashboard.activeDashboardParts
    );
    const end = start + WORKER_CONSTANTS.WORKER_CHUNK;
    for (let i = start; i < end && i < parts.length; i++) {
      this.createDashboardPartWorker(parts[i], i);
    }
  }

  /**
   * Create dashboard part worker
   * @param {Object} dashboardPart
   * @param {Number} index
   * @param {Boolean} printingMode
   */
  createDashboardPartWorker(dashboardPart: any, index: number, printingMode: boolean) {
    if (typeof Worker !== 'undefined') {
      const reportId = dashboardPart.reportId || '';
      reportFetcher.validateReportId(reportId).then(reportStatus => {
        if (!reportStatus.success) {
          const e = {
            data :{
              id: dashboardPart.id,
              messages: reportStatus.messages,
              isSuccess: reportStatus.success
            }
          };
          const dashboardWorker = new DashboardWorker();
          this.getDashboardPartData(dashboardWorker,index, e)
        } else {
          const dashboardWorker = new DashboardWorker();
          dashboardWorker.onmessage = this.getDashboardPartData.bind(this, dashboardWorker, index);
          dashboardWorker.postMessage(getPostData.apply(this));

          // Add dashboard worker list which is used for drilldown
          this.dashboardWorkerPendingList.push({
            dashboardPart: dashboardPart,
            worker: dashboardWorker,
            index: index
          });
        }
      })
    }

    function getPostData() {
      const crossFiltering = this.dashboardDetail.getCrossFilteringForDashboardPart(dashboardPart, true);

      return {
        type: 'DASHBOARD_PART',
        dashboardPart: JSON.parse(JSON.stringify(dashboardPart)),
        reportId: dashboardPart.reportId,
        reportPartId: dashboardPart.reportPartId,
        reportIdForLoad: printingMode ? dashboardPart.reportId : null,
        printingMode: printingMode,
        apiInfo: getApiInfo(),
        filters: isEmpty(crossFiltering) ? [] : crossFiltering
      };
    }
  }

  /**
   * Get dashboard part data
   * @param {Object} dashboardWorker
   * @param {Number} index
   * @param {Object} event
   */
  @action('getDashboardPartData')
  getDashboardPartData(dashboardWorker: any, index: number, event: any) {
    let { id, reportDef, reportPartDef, dashboardPartData, embeddedReports, messages, isSuccess } = event.data;

    // Remove pending dashboard worker in list
    this.dashboardWorkerPendingList.splice(
      findIndex(dashboardWorker => dashboardWorker.dashboardPart.id === id, this.dashboardWorkerPendingList),
      1
    );

    let part = find({ id: id }, this.dashboardDetail.dashboard.activeDashboardParts);
    if (isSuccess === false) {
      // Messages level : Just 1 message required for now
      let mesgText = localizer.getText('REPORTS_CONSTANTS_REPORT_MESSAGE_INVALID_REPORT');
      let messageKey = null;
      if (!isNil(messages) && isArray(messages) && !isEmpty(messages)) {
        const { messages: msg, key } = head(messages);
        if (!isEmpty(key)) {
          mesgText = head(msg);
          messageKey = key;
        }
      }

      reportDef = {
        detail: {
          message: mesgText,
          messageKey
        }
      };
      part.readReportInfo(reportDef);
    } else {
      const { reportFilter } = this.dashboardFilterStore;

      part.readReportInfo(reportDef, reportPartDef);
      this.loadOperatorGroupPromise().then(operatorGroup => {
        part.buildConfigFieldList(operatorGroup, reportFilter.markUpdateResult.bind(reportFilter));
      });

      part.reportPart.startLoadData();
      part.reportPart.colorPalettes = this.colorPalettes;
      this.removeRelatedDrillDownInfo(part);

      if(dashboardPartData && dashboardPartData.reportDefinition) {
        const reportPartDefinition = find({ id: part.reportPart.id }, dashboardPartData.reportDefinition.reportPart);
        const tempStateData = part.reportPart.getTemporaryStateData();
        part.readReportInfo(dashboardPartData.reportDefinition, reportPartDefinition);
        part.reportPart.restoreTemporaryStateData(tempStateData);
      }

      part.reportPart.setQueryData(dashboardPartData);
      this.getEmbeddedReports(part, embeddedReports);
    }

    // Start other worker
    (index + 1) % WORKER_CONSTANTS.WORKER_CHUNK === 0 && this.startDashboardDataWorker(index + 1);

    // Terminate worker after complete its task.
    dashboardWorker.terminate();
  }

  /**
   * Get embedded reports
   * @param {Object} dashboardPart
   * @param {Array} embeddedReports
   */
  getEmbeddedReports(dashboardPart: any, embeddedReports: Array<any>) {
    const embededReportDef = {
      reportParts: reduce(
        function (acc, item) {
          return set(item.id, item, acc);
        },
        {},
        compact(embeddedReports.reportParts)
      ),
      reports: reduce(
        function (acc, item) {
          const report = item;
          return set(report.id, report.reportPart, acc);
        },
        {},
        compact(embeddedReports.reports)
      )
    };

    const { reportPart, disableViewReport } = dashboardPart;
    if (isNil(reportPart) || disableViewReport) {
      return;
    }

    reportPart.readEmbededReportDef(embededReportDef);
  }

  /**
   * Get api terminateAllWorker
   */
  @action('terminateAllWorker')
  terminateAllWorker() {
    forEach((dashboardItem: any) => {
      dashboardItem.worker.terminate();
    }, this.dashboardWorkerPendingList);

    this.dashboardWorkerPendingList = [];
    this.relatedDashboardPartIds = [];
  }

  /**
   * export dashboard
   * @param  {string} dashboardId  dashboard id
   * @param  {string} format
   * @param  {any} successCallBack
   * @param  {any} failCallBack
   * @return {Promise}
   */
  exportDashboard(dashboardId: string, format: string, successCallBack: any, failCallBack: any): any {
    return exportFetcher.export(
      {
        dashboardId,
        format,
        tenantId: uiState.tenantId
      },
      successCallBack,
      failCallBack
    );
  }

  /**
   * Print dashboard
   * @param {String} dashboardId
   * @param {String} format
   * @param {Object} successCallBack
   * @param {Object} failCallBack
   * @return {Promise}
   */
  printDashboard(dashboardId: string, format: string, successCallBack: any, failCallBack: any): any {
    return exportFetcher
      .export({
        dashboardId,
        format,
        tenantId: uiState.tenantId
      })
      .then(data => {
        successCallBack();
        PrintingUtils.printHtml(data);
      }, failCallBack);
  }

  /**
   * Set color palettes
   * @param {Array} palettes
   */
  setColorPalettes(palettes: Array) {
    isEmpty(this.colorPalettes) && (this.colorPalettes = palettes);
  }

  /**
   * Load operator group promise builder
   */
  loadOperatorGroupPromise = () => {
    let operatorGroupPromise = () => Promise.resolve(this.operatorGroup);
    if (isNil(this.operatorGroup)) {
      operatorGroupPromise = this.operatorGroupPromise;
    }
    return operatorGroupPromise();
  };

  /**
   * Update use cache value
   * @param {bool} value
   */
  updateUseCache(value: boolean) {
    this.cacheInAPI = value;
    return Promise.resolve(this.cacheInAPI);
  }

  /**
   * Load dashboard
   * @param {Object} id[dashboard id]
   * @returns {Object}
   */
  @action('loadDashboardByID')
  async  loadDashboardByID (id: string)   {
    const dashboardParams = { dashboardId: id, action: 1 };
    const dashboardData = await fetcher.loadDashboard(dashboardParams).get('dashboard');
    this.readDashboardDefinition(dashboardData,null,true);
    return Promise.resolve(dashboardData);
  }
}



// WEBPACK FOOTER //
// ./ui/logic/Izenda.DashboardStore.js